import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, ArrowRight, Check, Lock, TrendingUp, Crown, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Reveal } from "../Reveal";
import { SIGNUP_URL, CHANNEL_PRICING, CHANNEL_PLANS, channelPrice, planDisplayName, planPath } from "../../data/content";
import { useCurrency } from "../../contexts/CurrencyContext";
import { convertFromINR } from "../../data/currencies";
import AnimatedNumber from "../AnimatedNumber";
import CurrencySelector from "../CurrencySelector";

// Slider stops and the price formula live in CHANNEL_PRICING (data/content)
// so the calculator, JSON-LD, /products.json and /llms.txt all agree.
const CHANNEL_STOPS = CHANNEL_PRICING.stops;
const WHITE_LABEL_MIN_CHANNELS = CHANNEL_PRICING.whiteLabelMinChannels;
const POPULAR_INDEX = 1; // 20 channels
// Plan name for each stop (Bronze, Silver, ...), matched on paid channels so
// the slider uses the same names as the comparison table.
const planFor = (s) => CHANNEL_PLANS.find((p) => p.pricing.yearly.amount === channelPrice(s.paid, "yearly"));
const planNameFor = (s) => planFor(s)?.name;
const TICK_ALIGN = (i) =>
  i === 0 ? "items-start" : i === CHANNEL_STOPS.length - 1 ? "items-end" : "items-center";

// Each row is tagged with what it's metered by, so agencies can see at a
// glance what scales unlimited vs. what's capped by the channel tier.
const GBP_ONLY_ROW = { label: "Automation Channel", sub: "Google Business Profile Management", tag: "Within Limit" };
const ALL_CHANNELS_ROW = { label: "Automation Channels", sub: "GBP, Facebook, Instagram, WhatsApp & WordPress", tag: "Within Limit" };
const MID_CHANNELS_ROW = { label: "Automation Channels", sub: "GBP, WhatsApp & WordPress", tag: "Within Limit" };
const FEATURE_ROWS = [
  { label: "QR Software Suite", tag: "Unlimited" },
  { label: "AI Content & Review Replies", tag: null },
  { label: "Instant Websites Included", tag: null, minChannels: CHANNEL_PRICING.whiteLabelMinChannels },
];

function BillingToggle({ billing, onChange }) {
  return (
    <div className="inline-flex items-center gap-1 rounded-full bg-[#F1F3F9] p-1.5">
      <button
        type="button"
        data-testid="pricing-billing-yearly"
        aria-pressed={billing === "yearly"}
        onClick={() => onChange("yearly")}
        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-all ${
          billing === "yearly"
            ? "bg-gradient-to-r from-[#FF6B00] to-[#FF8533] text-white shadow-[0_6px_18px_rgba(255,107,0,0.4)]"
            : "text-[#64748B] hover:text-[#0B1533]"
        }`}
      >
        First Year
      </button>
      <button
        type="button"
        data-testid="pricing-billing-lifetime"
        aria-pressed={billing === "lifetime"}
        onClick={() => onChange("lifetime")}
        className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-extrabold transition-all ${
          billing === "lifetime"
            ? "bg-white text-[#0B1533] shadow-[0_2px_10px_rgba(11,21,51,0.08)]"
            : "text-[#64748B] hover:text-[#0B1533]"
        }`}
      >
        <Sparkles className="mb-sparkle-icon w-3.5 h-3.5 text-[#FF6B00]" />
        <span className="mb-gradient-text">Lifetime</span>
      </button>
    </div>
  );
}

export default function PricingLocationSlider({ initialBilling = "yearly", onBillingChange }) {
  const [index, setIndex] = useState(POPULAR_INDEX);
  const [billing, setBillingState] = useState(initialBilling);
  const setBilling = (next) => {
    setBillingState(next);
    onBillingChange?.(next);
  };
  const { currency } = useCurrency();
  const money = (n) => `${currency.symbol}${Math.round(n).toLocaleString("en-US")}`;

  const stop = CHANNEL_STOPS[index];
  const channels = stop.total;
  const whiteLabel = channels >= WHITE_LABEL_MIN_CHANNELS;
  const featureRows = [
    channels >= CHANNEL_PRICING.allChannelsMinChannels
      ? ALL_CHANNELS_ROW
      : channels >= CHANNEL_PRICING.whiteLabelMinChannels
        ? MID_CHANNELS_ROW
        : GBP_ONLY_ROW,
    ...FEATURE_ROWS.filter((r) => !r.minChannels || channels >= r.minChannels),
  ];
  const isLifetime = billing === "lifetime";

  const totalAmount = useMemo(() => {
    return convertFromINR(channelPrice(stop.paid, isLifetime ? "lifetime" : "yearly"), currency);
  }, [isLifetime, stop, currency]);

  const perChannel = useMemo(() => totalAmount / stop.paid, [totalAmount, stop]);

  return (
    <section className="relative pt-6 pb-6 md:pt-8">
      <div className="max-w-3xl mx-auto px-6">
        <Reveal>
          <div
            data-testid="pricing-channel-calculator"
            className="relative overflow-hidden rounded-3xl border border-[#E6E9F1] bg-white mb-card-shadow p-8 md:p-10"
          >
            <div className="mb-blob mb-blob-orange mb-anim-a w-[280px] h-[280px] -top-32 -right-20 opacity-60" />

            <div className="relative flex justify-center">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#FF6B00] to-[#FF8533] px-4 py-1.5 text-xs font-bold text-white shadow-[0_6px_16px_rgba(255,107,0,0.35)]">
                <Zap className="w-3.5 h-3.5" fill="currentColor" />
                Channel Based
              </span>
            </div>

            <h3 className="relative mt-5 text-center text-lg md:text-xl font-bold text-[#0B1533]">
              How many channels do you need to manage?
            </h3>

            <div className="relative mt-6 flex flex-wrap items-center justify-center gap-3">
              <BillingToggle billing={billing} onChange={setBilling} />
              <CurrencySelector />
            </div>

            {/* Slider */}
            <div className="relative mt-10 px-2 md:px-6">
              <input
                type="range"
                min={0}
                max={CHANNEL_STOPS.length - 1}
                step={1}
                value={index}
                onChange={(e) => setIndex(Number(e.target.value))}
                data-testid="pricing-channel-slider"
                aria-label="Number of channels"
                className="mb-orange-range w-full"
                style={{
                  "--fill": `${(index / (CHANNEL_STOPS.length - 1)) * 100}%`,
                }}
              />
              <div className="mt-3 flex justify-between gap-1">
                {CHANNEL_STOPS.map((s, i) => (
                  <button
                    key={s.total}
                    type="button"
                    onClick={() => setIndex(i)}
                    className={`flex flex-col gap-1 ${TICK_ALIGN(i)} text-[11px] leading-tight md:text-sm font-semibold transition-colors whitespace-nowrap ${
                      index === i ? "text-[#FF6B00]" : "text-[#94A3B8] hover:text-[#475569]"
                    }`}
                  >
                    <span
                      className={`text-[10px] md:text-xs font-extrabold tracking-wide ${
                        index === i ? "text-[#FF6B00]" : "text-[#0B1533]"
                      }`}
                    >
                      {planNameFor(s)}
                    </span>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Price */}
            <div className="relative mt-8 text-center">
              <div className="flex items-baseline justify-center gap-2">
                <AnimatedNumber
                  value={totalAmount}
                  format={money}
                  data-testid="pricing-channel-price"
                  className="text-4xl md:text-5xl font-extrabold text-[#0B1533] tracking-tight"
                />
                {isLifetime ? (
                  <span className="inline-flex items-center gap-1 text-sm font-extrabold">
                    <Sparkles className="mb-sparkle-icon w-3.5 h-3.5 text-[#FF6B00]" />
                    <span className="mb-gradient-text">/lifetime</span>
                  </span>
                ) : (
                  <span className="text-sm font-semibold text-[#94A3B8]">/yr</span>
                )}
              </div>
              {isLifetime ? (
                <p className="mt-1 text-sm text-[#475569]">
                  <AnimatedNumber value={perChannel} format={money} className="font-semibold text-[#FF6B00]" />{" "}
                  per paid channel, one-time
                </p>
              ) : (
                <p className="mt-1 text-sm text-[#475569]">
                  Renewal Price{" "}
                  <AnimatedNumber value={perChannel} format={money} className="font-semibold text-[#FF6B00]" />{" "}
                  per Active channel per year
                </p>
              )}
              <p className="mt-2 inline-flex items-center rounded-full bg-[#FFF3E9] px-3 py-1 text-xs font-bold text-[#0B1533]">
                {planNameFor(stop) && <span className="mr-1.5 text-[#FF6B00]">{planNameFor(stop)} &middot;</span>}
                {stop.label} {stop.free > 0 ? "Accounts" : "Channels"} {index === POPULAR_INDEX && <span className="ml-1.5 text-[#FF6B00]">&middot; Most Popular</span>}
              </p>
            </div>

            {/* The plan's detail page */}
            {planFor(stop) && (
              <div className="mt-6 flex justify-center">
                <Button
                  data-testid="pricing-channel-plan-link"
                  className="h-10 w-full shrink-0 rounded-full bg-[#0B1533] px-5 text-sm font-bold text-white hover:-translate-y-0.5 hover:bg-[#1a2547] hover:shadow-[0_10px_24px_rgba(11,21,51,0.25)] transition-all sm:w-auto"
                  asChild
                >
                  <Link to={planPath(planFor(stop))}>
                    {planDisplayName(planFor(stop).name)} Plan Details <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            )}

            {/* Resell upside */}
            <div className="relative mt-4 flex items-center gap-4 overflow-hidden rounded-2xl bg-[#0B1533] px-5 py-4 text-left">
              <span aria-hidden className="pointer-events-none absolute -right-10 -top-16 h-40 w-40 rounded-full bg-[#FF6B00]/30 blur-3xl" />
              <div className="relative shrink-0 text-center">
                <div className="text-3xl font-extrabold leading-none tracking-tight text-[#FF8533] md:text-4xl">10x</div>
                <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-white/60">
                  <TrendingUp className="h-3 w-3" /> Resale
                </div>
              </div>
              <span aria-hidden className="relative h-10 w-px shrink-0 bg-white/15" />
              <p className="relative text-sm leading-snug text-[#CBD5E1]">
                <span className="font-bold text-white">Agencies earn up to 10x what they pay per channel.</span>{" "}
                Resell every automation, QR and business software channel to clients.
              </p>
            </div>

            {/* Features */}
            <div className="relative mt-8 flex items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#94A3B8]">What&apos;s included</span>
              <span className="h-px flex-1 bg-[#E6E9F1]" />
            </div>
            <div className="relative mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {featureRows.map((item) => (
                <div
                  key={item.label}
                  className="flex items-start gap-3 rounded-2xl border border-[#E6E9F1] bg-[#F9FAFC] p-4 text-left"
                >
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#FF6B00]/10">
                    <Check className="h-3.5 w-3.5 text-[#FF6B00]" strokeWidth={3} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-sm font-bold leading-snug text-[#0B1533]">{item.label}</span>
                      {item.tag && (
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide whitespace-nowrap ${
                            item.tag === "Unlimited" ? "bg-[#FF6B00]/10 text-[#FF6B00]" : "bg-[#E6E9F1] text-[#64748B]"
                          }`}
                        >
                          {item.tag}
                        </span>
                      )}
                    </div>
                    {item.sub && <div className="mt-1 text-xs leading-snug text-[#64748B]">{item.sub}</div>}
                  </div>
                </div>
              ))}
            </div>

            {/* White-label - called out separately since it's the core agency value prop */}
            <div
              data-testid="pricing-white-label-highlight"
              className={`relative mt-3 flex items-center gap-3 rounded-2xl border p-4 ${
                whiteLabel
                  ? "border-[#FF6B00] bg-gradient-to-r from-[#FFF3E9] to-white"
                  : "border-dashed border-[#CBD2E0] bg-[#F9FAFC]"
              }`}
            >
              <span
                className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${
                  whiteLabel ? "bg-[#FF6B00] shadow-[0_6px_14px_rgba(255,107,0,0.35)]" : "bg-[#E6E9F1]"
                }`}
              >
                {whiteLabel ? (
                  <Crown className="w-5 h-5 text-white" strokeWidth={2.5} />
                ) : (
                  <Lock className="w-5 h-5 text-[#94A3B8]" strokeWidth={2.5} />
                )}
              </span>
              <div className="min-w-0 flex-1 text-left">
                <div className={`text-sm font-bold ${whiteLabel ? "text-[#0B1533]" : "text-[#64748B]"}`}>
                  White-Label Platform & Custom Domain
                </div>
                <div className="text-xs text-[#64748B]">
                  {whiteLabel
                    ? "Launch under your own brand"
                    : `Unlocks at ${WHITE_LABEL_MIN_CHANNELS}+ channels`}
                </div>
              </div>
              <span
                className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                  whiteLabel ? "bg-[#FF6B00] text-white" : "bg-[#E6E9F1] text-[#64748B]"
                }`}
              >
                {whiteLabel ? "Included" : "Locked"}
              </span>
            </div>

            <div className="relative mt-8 flex justify-center">
              <Button
                data-testid="pricing-channel-cta"
                className="w-full sm:w-auto font-semibold rounded-full h-12 px-8 bg-[#FF6B00] hover:bg-[#FF8533] text-white hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(255,107,0,0.35)] transition-all"
                asChild
              >
                <a href={SIGNUP_URL}>
                  Get Started with {stop.label} Channels <ArrowRight className="ml-1.5 w-4 h-4" />
                </a>
              </Button>
            </div>

            <div className="relative mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-semibold">
              <Link to="/features" className="inline-flex items-center gap-1 text-[#475569] hover:text-[#FF6B00]">
                View Detailed Features <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <span aria-hidden className="hidden h-4 w-px bg-[#E6E9F1] sm:block" />
              <a href="#pricing-comparison" className="inline-flex items-center gap-1 text-[#475569] hover:text-[#FF6B00]">
                Compare in Pricing Table <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
